// src/components/whitepaper/WhitepaperCoverIllustration.tsx
//
// Illustration SaaS pour la couverture du livre blanc (fallback inline
// quand aucune image PNG n'est uploadée). Rendu en SVG + Tailwind.
//
// Design :
//   - Ratio 3:4 (proportions livre)
//   - Background gradient brand pastel + grille subtile (effet "data viz")
//   - Badge "Livre blanc" gradient en haut
//   - Chiffre central "12" XXL en gradient brand
//   - Sous-titre + sources
//   - Éléments décoratifs SaaS : barres de stats, points/dots, cercles flottants

interface Props {
  /** Titre principal (ex: "12 chiffres qui changent…") — split par le premier nombre */
  title: string;
  /** Sous-titre court */
  subtitle?: string;
  /** Sources affichées en bas (ex: "INSEE · APEC · DARES") */
  sources?: string;
  /** Nombre de pages affiché (badge) */
  pageCount?: number;
  /** Texte du focal point (ex: "12", "0 €", "5×"). Si absent : extrait du
   *  titre, sinon "•" (pas de fallback "12" hardcodé bizarre). */
  focalNumber?: string;
}

export default function WhitepaperCoverIllustration({
  title,
  subtitle,
  sources,
  pageCount,
  focalNumber,
}: Props) {
  // Priorité : prop explicite > premier nombre du titre > fallback "•"
  const numberMatch = title.match(/\d+/);
  const bigNumber = focalNumber ?? (numberMatch ? numberMatch[0] : '•');

  // Si on a tiré le focal du titre, on tronque le titre. Sinon on garde le titre complet.
  const titleAfterFocal =
    !focalNumber && numberMatch
      ? title.slice(numberMatch.index! + numberMatch[0].length).trim().replace(/^[,. ]+/, '')
      : title;

  // Limite le subtitle pour qu'il ne déborde pas dans l'illustration (contrainte
  // par aspect-ratio 3:4). Le formulaire à droite affiche déjà le subtitle
  // complet — ici c'est un teaser visuel.
  const subtitleTeaser =
    subtitle && subtitle.length > 120
      ? subtitle.slice(0, 117).replace(/[,. ]*$/, '') + '…'
      : subtitle;

  return (
    <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]">
      {/* Grille subtile background (effet data viz) */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#30475e" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Blobs gradient décoratifs */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#d03484]/25 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-16 -left-12 w-48 h-48 rounded-full bg-[#d47254]/25 blur-3xl"
      />

      {/* Petites bars de stats flottantes (haut-gauche) */}
      <div
        aria-hidden
        className="absolute top-[18%] left-6 flex items-end gap-1.5 opacity-60"
      >
        {[16, 26, 20, 32, 22].map((h, i) => (
          <div
            key={i}
            className="w-1.5 rounded-sm bg-gradient-to-t from-[#d03484] to-[#d47254]"
            style={{ height: `${h}px` }}
          />
        ))}
      </div>

      {/* Points flottants (bas-droit) */}
      <div
        aria-hidden
        className="absolute bottom-[22%] right-6 grid grid-cols-4 gap-1.5 opacity-50"
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="block w-1 h-1 rounded-full bg-[#30475e]"
          />
        ))}
      </div>

      {/* Mini-card "métrique" flottante (haut-droit, effet SaaS dashboard) */}
      <div
        aria-hidden
        className="absolute top-6 right-6 rounded-xl bg-white/85 backdrop-blur-sm ring-1 ring-white/60 shadow-lg px-3 py-2 hidden sm:block"
      >
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[9px] font-semibold uppercase tracking-widest text-neutral-500">
            Marché 2026
          </span>
        </div>
        <div className="text-sm font-bold text-[#30475e] mt-0.5">+4,2 %</div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className="relative z-10 h-full flex flex-col p-7 md:p-8">
        {/* Top : badge livre blanc */}
        <div className="inline-flex items-center self-start gap-1.5 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-white shadow-md whitespace-nowrap">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0l2.351 9.649L24 12l-9.649 2.351L12 24l-2.351-9.649L0 12l9.649-2.351L12 0z" />
          </svg>
          Livre blanc
        </div>

        {/* Brand mark */}
        <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-neutral-400">
          recruteurs.io
        </div>

        {/* Focal point : grand chiffre dans un cercle gradient */}
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="relative">
            {/* Cercle gradient avec chiffre (texte de focal) */}
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-2xl shadow-[#d03484]/30 px-2">
              <span
                className={`font-extrabold text-white leading-none whitespace-nowrap ${
                  bigNumber.length <= 2
                    ? 'text-5xl md:text-6xl'
                    : bigNumber.length <= 4
                    ? 'text-3xl md:text-4xl'
                    : 'text-2xl md:text-3xl'
                }`}
              >
                {bigNumber}
              </span>
            </div>
            {/* Petits sparkles flottants autour du cercle */}
            <span
              aria-hidden
              className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-white ring-2 ring-[#d03484]/30"
            />
            <span
              aria-hidden
              className="absolute -bottom-2 -left-3 w-3 h-3 rounded-full bg-[#fde7f2] ring-2 ring-white"
            />
          </div>

          {/* Titre éditorial */}
          <h3 className="mt-5 text-base md:text-lg font-bold text-[#30475e] leading-tight max-w-[18ch]">
            {titleAfterFocal || title}
          </h3>

          {subtitleTeaser && (
            <p
              className="mt-2 text-[11px] md:text-xs text-neutral-500 leading-relaxed max-w-[22ch] overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {subtitleTeaser}
            </p>
          )}
        </div>

        {/* Bottom : sources + pages */}
        <div className="flex items-center justify-between text-[10px] text-neutral-500 pt-3 border-t border-neutral-300/40">
          <span className="inline-flex items-center gap-1 font-semibold">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            {pageCount ?? 28} pages
          </span>
          {sources && (
            <span className="truncate ml-2 text-right">{sources}</span>
          )}
        </div>
      </div>
    </div>
  );
}
